// Native
import {Badge, HStack, Progress, Separator, Stack, Text, useDisclosure} from "@chakra-ui/react"

// Custom
import {Parts, UI} from "components"
import {Pressable} from "components/ui"
import useNetworkReport from "hooks/useNetworkReport"
import {Styles} from "config"

export function Resume({children, ...props}) {

    const report = useNetworkReport()

    return (
        <UI.Box loading={report.isLoading} {...props}>
            <Text children="Rede:" color="GrayText"/>
            <Text children="Abaixo de você:" color="GrayText" fontSize="xs"/>
            <Text fontSize="lg" fontWeight="bold">{report.data?.downlines} consultores</Text>
            <Separator my={4}/>
            <Text children="Downlines diretos:" color="GrayText" fontSize="xs"/>
            <Text fontSize="lg" fontWeight="bold">{report.data?.direct_downlines} downlines diretos</Text>
            {children}
        </UI.Box>
    )
}

export function Analytics(props) {

    const report = useNetworkReport()
    const dialogBlack = useDisclosure()

    const active_percentage = report.data?.active_percentage < 0 ? 0 : report.data?.active_percentage > 100 ? 100 : report.data?.active_percentage
    const inactive_percentage = report.data?.inactive_percentage < 0 ? 0 : report.data?.inactive_percentage > 100 ? 100 : report.data?.inactive_percentage

    const bnf100Data = Array.isArray(report.data?.bnf_100) && report.data?.bnf_100.length > 0
        ? report.data?.bnf_100[0]
        : null
    const bnf100Count = bnf100Data?.qty_downlines_with_goal || 0
    const bnf100Percentage = Math.min((bnf100Count / 5) * 100, 100)

    return (
        <>
            <Parts.Dialog.Reports.BlackBNF {...dialogBlack} />
            <UI.Box loading={report.isLoading} {...props}>
                <Text color="GrayText">Relatório avançado: <Badge colorPalette="orange">Novidade</Badge></Text>

                <Stack gap={2}>

                    <Progress.Root
                        striped animated
                        defaultValue={active_percentage}
                        maxW="sm" mt={2}
                        colorPalette="green"
                    >
                        <HStack gap="5">
                            <Progress.Label>Rede ativa</Progress.Label>
                            <Progress.Track flex="1">
                                <Progress.Range/>
                            </Progress.Track>
                            <Progress.ValueText>{active_percentage}%</Progress.ValueText>
                        </HStack>
                        <Text
                            children={`${report.data?.active_count} consultores ativos`}
                            color="GrayText"
                            fontSize="xs"
                        />
                    </Progress.Root>

                    <Progress.Root
                        striped animated
                        defaultValue={inactive_percentage}
                        maxW="sm" mt={2}
                        colorPalette="red"
                    >
                        <HStack gap="5">
                            <Progress.Label>Rede ociosa</Progress.Label>
                            <Progress.Track flex="1">
                                <Progress.Range/>
                            </Progress.Track>
                            <Progress.ValueText>{inactive_percentage}%</Progress.ValueText>
                        </HStack>
                        <Text
                            children={`${report.data?.inactive_count} estão inativos ou não fazem compras há muito tempo.`}
                            color="GrayText" fontSize="xs"/>
                    </Progress.Root>

                    <Separator/>

                    <UI.Pressable onClick={dialogBlack.onOpen}>
                        <Progress.Root value={bnf100Percentage} maxW="sm" mt={2} colorPalette="blue">
                            <HStack gap="5">
                                <Progress.Label>Black BNF (100)</Progress.Label>
                                <Progress.Track flex="1">
                                    <Progress.Range/>
                                </Progress.Track>
                                <Progress.ValueText>
                                    {Math.round(bnf100Percentage)}%
                                </Progress.ValueText>
                            </HStack>
                            <Text
                                children={`${bnf100Count} consultores`}
                                color="GrayText"
                                fontSize="xs"
                            />
                        </Progress.Root>
                    </UI.Pressable>

                    <UI.Pressable onClick={dialogBlack.onOpen}>
                        <Progress.Root value={report.data?.bnf_200?.percentage || 0} maxW="sm" mt={2}
                                       colorPalette="blue">
                            <HStack gap="5">
                                <Progress.Label>Black BNF (200)</Progress.Label>
                                <Progress.Track flex="1">
                                    <Progress.Range/>
                                </Progress.Track>
                                <Progress.ValueText>
                                    {Array.isArray(report.data?.bnf_200) ? '0%' : `${report.data?.bnf_200?.percentage || 0}%`}
                                </Progress.ValueText>
                            </HStack>
                            <Text
                                children={Array.isArray(report.data?.bnf_200) ? '0 consultores' : `${report.data?.bnf_200?.count || 0} consultores`}
                                color="GrayText"
                                fontSize="xs"
                            />
                        </Progress.Root>
                    </UI.Pressable>

                    <UI.Pressable onClick={dialogBlack.onOpen}>
                        <Progress.Root value={report.data?.bnf_400?.percentage || 0} maxW="sm" mt={2}>
                            <HStack gap="5">
                                <Progress.Label>Black BNF (400)</Progress.Label>
                                <Progress.Track flex="1">
                                    <Progress.Range/>
                                </Progress.Track>
                                <Progress.ValueText>
                                    {Array.isArray(report.data?.bnf_400) ? '0%' : `${report.data?.bnf_400?.percentage || 0}%`}
                                </Progress.ValueText>
                            </HStack>
                            <Text
                                children={Array.isArray(report.data?.bnf_400) ? '0 consultores' : `${report.data?.bnf_400?.count || 0} consultores`}
                                color="GrayText"
                                fontSize="xs"
                            />
                        </Progress.Root>
                    </UI.Pressable>

                </Stack>
            </UI.Box>
        </>
    )
}

export function Pins(props) {

    const report = useNetworkReport()
    const dialog = useDisclosure()

    return (
        <>
            <Parts.Dialog.Reports.Pins {...dialog} />
            <UI.Box loading={report.isLoading} {...props}>
                <Text color="GrayText">Relatório de pins: <Badge colorPalette="orange">Novidade</Badge></Text>

                <Stack gap={0}>

                    {report.data?.careers?.map(item => {

                        const style = Styles.pin(item.career.id)

                        if (item.count <= 0 || item.career.id === 1) return <></>

                        return (
                            <Pressable key={item?.career?.id} onClick={dialog.onOpen}>
                                <Progress.Root value={item.percentage <= 0 ? 5 : item.percentage} maxW="sm" mt={2}
                                               colorPalette={style.colorPalette}>
                                    <Progress.Label fontSize="xs">{item?.career?.title}</Progress.Label>
                                    <Progress.Track flex="1">
                                        <Progress.Range/>
                                    </Progress.Track>
                                </Progress.Root>
                                <Text mt={1} fontSize="xs" color="fg.muted">{item.count} consultores</Text>
                            </Pressable>
                        )
                    })}

                </Stack>
            </UI.Box>
        </>
    )
}